// O que aconteceu depois que a trava ligou — somente leitura.
//
// É o que se manda ao banco quando ele contesta uma recusa: instante, vínculo,
// produto, valor pedido e o trace da tentativa. Duas regras mandam no desenho:
//
//  1. FALHA DE LEITURA NUNCA VIRA ZERO. `erro_ao_ler_recusas` faz os totais
//     aparecerem como "—". Zero seria lido como "ninguém foi recusado", que é a
//     leitura errada mais cara deste painel.
//  2. A ATUALIZAÇÃO AUTOMÁTICA NÃO MEXE NO RECORTE. Os filtros e a página vivem
//     em estado próprio e entram na chave de cache; o refetch de 30s troca os
//     dados, nunca o que o operador montou para ler.
//
// Os filtros ficam separados do seletor de ativação de propósito: consultar o
// histórico de uma filial não pode mexer na unidade que se vai ligar.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, DataTable, Pagination, Pill, type Column } from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { traduzirErroDaTrava } from "../../lib/trava-margem-erros";
import type { Unidade } from "./trava-margem-unidades";

const brl = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const quando = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString("pt-BR") : "—");

type LinhaUnidade = NonNullable<Awaited<ReturnType<typeof atlas.admin.trava.monitor>>["unidades"]>[number];
type LinhaRecusa = NonNullable<
  NonNullable<Awaited<ReturnType<typeof atlas.admin.trava.monitor>>["recusas"]>["itens"]
>[number];

export function TravaMargemMonitor({ unidades }: { unidades: Unidade[] }) {
  // Recorte do operador. Fora da query de propósito — é o que sobrevive ao
  // refetch automático.
  const [fUnidade, setFUnidade] = useState<number | null>(null);
  const [fCompetencia, setFCompetencia] = useState("");
  const [fDesde, setFDesde] = useState("");
  const [fAte, setFAte] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(25);

  const monitor = useQuery({
    queryKey: ["admin", "trava", "monitor", fUnidade, fCompetencia, fDesde, fAte, pagina, porPagina],
    queryFn: () =>
      atlas.admin.trava.monitor({
        ...(fUnidade != null ? { unidadeId: fUnidade } : {}),
        ...(fCompetencia ? { competencia: fCompetencia } : {}),
        ...(fDesde ? { desde: new Date(fDesde + "T00:00:00").toISOString() } : {}),
        ...(fAte ? { ate: new Date(fAte + "T23:59:59").toISOString() } : {}),
        pagina,
        porPagina,
      }),
    refetchInterval: 30_000,
    // Mantém a página anterior à vista enquanto a nova chega: sem isto a tabela
    // pisca em vazio a cada 30s e o operador perde a linha que estava lendo.
    placeholderData: (anterior) => anterior,
  });

  const temFiltro = fUnidade != null || !!fCompetencia || !!fDesde || !!fAte;
  const limpar = () => {
    setFUnidade(null);
    setFCompetencia("");
    setFDesde("");
    setFAte("");
    setPagina(1);
  };

  const erroDeFiltro = monitor.error ? traduzirErroDaTrava(monitor.error) : null;
  const dados = monitor.data;
  const falhouLeitura = !!dados?.erro_ao_ler_recusas;

  const colunasUnidades: Column<LinhaUnidade>[] = [
    {
      key: "prefeitura",
      header: "Unidade",
      render: (u) => u.prefeitura ?? String(u.prefeitura_id),
      wrap: true,
    },
    {
      key: "trava",
      header: "Trava",
      render: (u) => (
        <Pill variant={u.trava_ativa ? "rejeitada" : "emdia"}>{u.trava_ativa ? "LIGADA" : "desligada"}</Pill>
      ),
    },
    {
      key: "na_ativacao",
      header: "Bloqueados na ativação",
      align: "right",
      render: (u) => u.vinculos_bloqueados_na_ativacao ?? "—",
    },
    {
      key: "agora",
      header: "Bloqueados agora",
      align: "right",
      render: (u) => u.vinculos_com_pendencia_agora ?? "—",
    },
    { key: "pendente", header: "Pendente agora", align: "right", render: (u) => brl(u.valor_pendente_agora) },
    { key: "competencia", header: "Competência", render: (u) => u.competencia_mais_recente ?? "—" },
    {
      key: "recusas",
      header: "Recusas (oficial)",
      align: "right",
      // `—` e não `0`: quando a leitura falhou, não se sabe. Zero afirmaria que
      // ninguém foi recusado, que é a conclusão errada mais cara aqui.
      render: (u) => (u.recusas ? `${u.recusas.total} (${u.recusas.vinculos_distintos} vínculo(s))` : "—"),
    },
    { key: "ligada_em", header: "Ligada em", render: (u) => quando(u.ativada_em) },
    { key: "por", header: "Por quem", render: (u) => u.ativada_por ?? "—" },
    {
      key: "justificativa",
      header: "Justificativa",
      wrap: true,
      render: (u) =>
        u.justificativa ?? <span style={{ color: "var(--text-dim)" }}>sem justificativa informada</span>,
    },
  ];

  const colunasRecusas: Column<LinhaRecusa>[] = [
    { key: "ts", header: "Quando", render: (r) => quando(r.ts) },
    { key: "unidade", header: "Unidade", render: (r) => r.prefeitura_id ?? "—" },
    { key: "matricula", header: "Matrícula", mono: true, render: (r) => r.matricula ?? "—" },
    { key: "competencia", header: "Competência", render: (r) => r.competencia ?? "—" },
    { key: "produto", header: "Produto", render: (r) => r.produto ?? "—" },
    { key: "solicitado", header: "Parcela pedida", align: "right", render: (r) => brl(r.valor_solicitado) },
    { key: "pendente", header: "Pendente", align: "right", render: (r) => brl(r.valor_pendente) },
    { key: "canal", header: "Canal", render: (r) => r.canal ?? "—" },
    { key: "trace", header: "Trace", mono: true, render: (r) => r.trace_id },
  ];

  return (
    <Card>
      <h2 style={{ margin: "0 0 4px", fontSize: 16 }}>Monitoramento</h2>
      <p style={{ margin: "0 0 12px", color: "var(--text-muted)", fontSize: 13 }}>
        Somente leitura. É daqui que sai a evidência quando uma consignatária contesta uma recusa.
      </p>

      {/* ---------- filtros ---------- */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
        <label style={{ display: "grid", gap: 2, fontSize: 13 }}>
          Unidade
          <select
            value={fUnidade ?? ""}
            onChange={(e) => {
              setFUnidade(e.target.value ? Number(e.target.value) : null);
              setPagina(1);
            }}
            style={{
              padding: 6,
              background: "var(--surface)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 6,
            }}
          >
            <option value="">Todas</option>
            {unidades.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nome}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: "grid", gap: 2, fontSize: 13 }}>
          Competência (AAAAMM)
          <input
            value={fCompetencia}
            onChange={(e) => {
              setFCompetencia(e.target.value.replace(/\D/g, "").slice(0, 6));
              setPagina(1);
            }}
            placeholder="202608"
            inputMode="numeric"
            style={{
              width: 96,
              padding: 6,
              background: "var(--surface)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 6,
            }}
          />
        </label>
        <label style={{ display: "grid", gap: 2, fontSize: 13 }}>
          De
          <input
            type="date"
            value={fDesde}
            onChange={(e) => {
              setFDesde(e.target.value);
              setPagina(1);
            }}
            style={{
              padding: 6,
              background: "var(--surface)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 6,
            }}
          />
        </label>
        <label style={{ display: "grid", gap: 2, fontSize: 13 }}>
          Até
          <input
            type="date"
            value={fAte}
            onChange={(e) => {
              setFAte(e.target.value);
              setPagina(1);
            }}
            style={{
              padding: 6,
              background: "var(--surface)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 6,
            }}
          />
        </label>
        {temFiltro && (
          <Button variant="ghost" size="sm" onClick={limpar}>
            Limpar filtros
          </Button>
        )}
      </div>

      {erroDeFiltro && (
        <p role="alert" style={{ color: "var(--danger-500)" }}>
          <strong>{erroDeFiltro.causa}</strong> {erroDeFiltro.proximaAcao}
          {erroDeFiltro.detalheTecnico && (
            <span style={{ display: "block", fontSize: 12, color: "var(--text-dim)" }}>
              Detalhe: {erroDeFiltro.detalheTecnico}
            </span>
          )}
        </p>
      )}

      {falhouLeitura && (
        <p role="alert" style={{ color: "var(--danger-500)" }}>
          Não foi possível ler as recusas persistidas: {dados!.erro_ao_ler_recusas}. Os totais aparecem como
          “—” <strong>de propósito</strong> — zero seria lido como “ninguém foi recusado”.
        </p>
      )}

      {dados && !dados.alguma_trava_ativa && (
        <p style={{ color: "var(--text-muted)" }}>Nenhuma unidade com a trava ligada.</p>
      )}

      <DataTable
        columns={colunasUnidades}
        rows={dados?.unidades ?? []}
        rowKey={(u) => String(u.prefeitura_id)}
        loading={monitor.isLoading}
        emptyState="Nenhuma unidade com trava registrada ou recusa no período."
      />

      {/* ---------- recusas, uma a uma ---------- */}
      <h3 style={{ margin: "24px 0 8px", fontSize: 15 }}>
        Recusas registradas{dados?.recusas ? ` — ${dados.recusas.total}` : ""}
      </h3>
      {dados?.atualizado_em && (
        <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--text-muted)" }}>
          Atualizado em {quando(dados.atualizado_em)}
        </p>
      )}

      <DataTable
        columns={colunasRecusas}
        rows={dados?.recusas?.itens ?? []}
        rowKey={(r) => r.id}
        loading={monitor.isLoading}
        emptyState={temFiltro ? "Nenhuma recusa com estes filtros." : "Nenhuma recusa registrada."}
      />

      {dados?.recusas && dados.recusas.total > 0 && (
        <Pagination
          total={dados.recusas.total}
          pagina={dados.recusas.pagina}
          porPagina={dados.recusas.por_pagina}
          onPagina={setPagina}
          onPorPagina={(n) => {
            setPorPagina(n);
            setPagina(1);
          }}
          rotulo="recusas"
        />
      )}

      {dados?.observacao_sobre_recusas && (
        <p style={{ marginTop: 12, fontSize: 12, color: "var(--text-muted)" }}>
          {dados.observacao_sobre_recusas}
        </p>
      )}
    </Card>
  );
}
